import {Component, Injector, Input, OnInit, Output} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {markFromGroupControlsAsTouchedAndDirty} from 'helpers';
import {
  StepData
} from 'models';
import { debounceTime, first } from 'rxjs/operators';
import { ValidatorsService } from 'services';
import { VERYMOBILE_ICCID_PREFIX } from '../../../../shared/constants/form.constants';
import { ProcessStepBaseComponent } from '../../_core/process-step-base.component';

@Component({
  selector: 'sb-step-digital-onboarding-iccid',
  templateUrl: './digital-onboarding-iccid.component.html',
  styleUrls: ['./digital-onboarding-iccid.component.scss']
})
export class DigitalOnboardingIccidComponent extends ProcessStepBaseComponent implements OnInit {
  shouldShowCamera = false;
  hasAlreadyShownCamera = false;
  cameraEnabled = false;
  @Input() fakeIccid: any = null;

  ICCID_PREFIX = VERYMOBILE_ICCID_PREFIX;
  public partialIccidForm: FormGroup;
  iccidlength = false;
  // We will scale the photo width to this
  canvasWidth = 400;
  // This will be computed based on the input stream
  canvasHeight = 200;

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit() {
    // super.shouldShowBackButton();
    this.partialIccidForm = new FormGroup({
      fakeIccid: new FormControl(undefined, ValidatorsService.iccidNoPrefix())
    });
    super.isStickyNavigation();
    super.setFormGroup(this.formsService.generateStepFdoIccidForm());
    super.setFormStoreDataFunction(this.appStoreFacadeService.step.storeStepFdoIccidValue);
    this.getFormDataAndValidation();
    this.cameraEnabled = this.environmentService.isPhotoCaptureEnabled();
  }

  onInputChange(newValue: string) {
    if (newValue !== null && newValue !== undefined && newValue !== '') {
      if (newValue.length <= 12) {
        this.iccidlength = true;
      } else {
        this.iccidlength = false;
      }
    } else {
      this.iccidlength = false;
    }
  }

  onInputFocusOut() {
    this.iccidlength = false;
  }

  public goToPreviousStep() {
    if (this.shouldShowCamera) {
      return this.hideCamera();
    }
    // Overrides because it should redirect to another place, instead of the previous step.
    this.appStoreFacadeService.navigation.goPrevious();
  }

  private getFormDataAndValidation() {
    this.subscriptions.add(
      this.appStoreFacadeService.step.fdoIccid$
      .pipe(first(Boolean))
        .subscribe((stepData: StepData) => {
          super.setFormGroupStepData(stepData);
          this.partialIccidForm.patchValue({
            iccid: (this.formGroup.get('fdoIccid.iccid').value || '').replace(this.ICCID_PREFIX, '')
          });
          // forza il valore di fdoIccid.iccid su fakeIccid
          this.partialIccidForm.patchValue({
            fakeIccid: (this.formGroup.get('fdoIccid.iccid').value || '').replace(this.ICCID_PREFIX, '')
          });
        })
    );

    this.subscriptions.add(
      this.appStoreFacadeService.step.fdoIccidValue$
        .pipe(debounceTime(150), first(Boolean))
        .subscribe(() => {
          // Set default
          this.sendAnalyticsData();
        })
    );
    this.subscriptions.add(
      this.partialIccidForm.valueChanges
        .subscribe(value => {
          this.formGroup.patchValue({
            fdoIccid: {
              iccid: this.ICCID_PREFIX.toString() + value.fakeIccid
            }
          });
        })
    );
  }

  // ##################################################################
  // Camera management

  onSelectImage() {
    const captureEl = document.getElementById('capture') as HTMLInputElement;
    const file = captureEl.files[0];
    const reader  = new FileReader();
    const MAX_FILE_SIZE = 1048576 * 8;
    reader.onloadend = () => {
      const base64 = reader.result;
      this.verifyPicture(base64);
    };

    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        this.appStoreFacadeService.errors.openErrorModal(
          'La foto è troppo grande. Caricare una foto a una risoluzione più bassa.', '',
          'Ops, qualcosa è andato storto!');
        return;
      }
      reader.readAsDataURL(file);
    } /*else {
      alert('No image selected');
    }*/
  }

  goToCamera() {
    document.getElementById('capture').click();
    this.sendAnalyticsData('fdo-camera');

    const input = document.getElementById('capture') as HTMLInputElement;
    input.onclick = () => {
      input.value = null;
    };
  }

  getCanvasElement(): HTMLCanvasElement {
    const canvas = document.getElementById('canvas') as HTMLCanvasElement;
    return canvas;
  }

  getVideoElement(): HTMLVideoElement {
    const video = document.getElementById('video') as HTMLVideoElement;
    return video;
  }

  getPhotoElement() {
    const photo = document.getElementById('photo') as HTMLElement;
    return photo;
  }

  // Fill the photo with an indication that none has been
  // captured.
  clearCanvas() {
    const canvas = this.getCanvasElement();
    const photo = this.getPhotoElement();
    const context = canvas.getContext('2d');
    context.fillStyle = '#AAA';
    context.fillRect(0, 0, canvas.width, canvas.height);
    const data = canvas.toDataURL('image/png');
    photo.setAttribute('src', data);
  }

  stopVideoStreaming() {
    const videoElem = this.getVideoElement();
    if (videoElem) {
      const stream = videoElem.srcObject as MediaStream;
      const tracks = stream.getTracks();
      tracks.forEach((track) => {
        track.stop();
      });
      videoElem.srcObject = null;
    }
  }

  // Capture a photo by fetching the current contents of the video
  // and drawing it into a canvas, then converting that to a PNG
  // format data URL. By drawing it on an offscreen canvas and then
  // drawing that to the screen, we can change its size and/or apply
  // other changes before drawing it.
  takePicture() {
    const canvas = this.getCanvasElement();
    const video = this.getVideoElement();
    const photo = this.getPhotoElement();
    const  context = canvas.getContext('2d');
    canvas.width = this.canvasWidth;
    canvas.height = this.canvasHeight;
    context.drawImage(video, 0, 0, this.canvasWidth, this.canvasHeight);

    const data = canvas.toDataURL('image/jpeg');
    // alert(data);
    photo.setAttribute('src', data);
    this.verifyPicture(data);
    // this.stopVideoStreaming();
  }

  private verifyPicture(base64: string | ArrayBuffer) {
    this.execService
      .readBarcode(base64.toString().replace('data:image/jpeg;base64,', ''))
      .subscribe(({iccid}) => {
        if (iccid) {
          iccid = iccid.slice(6);
          this.formGroup.patchValue({
            fdoIccid: {
              iccid
            }
          });
          this.partialIccidForm.patchValue({
            fakeIccid: iccid.replace(this.ICCID_PREFIX.toString(), '')
          });
          this.formGroup.get('fdoIccid.iccid').updateValueAndValidity();
          markFromGroupControlsAsTouchedAndDirty(this.formGroup);
          this.partialIccidForm.get('fakeIccid').updateValueAndValidity();
          markFromGroupControlsAsTouchedAndDirty(this.partialIccidForm);
        } else {
          this.appStoreFacadeService.errors.openErrorModal(
            'Verifica che il codice ICCID sia ben visibile mentre scatti la foto. In caso contrario digitalo manualmente.', '',
            'Ops, qualcosa è andato storto!');
          // console.log("not detected");
        }
        this.hideCamera();
      });
  }

  private hideCamera() {
    this.shouldShowCamera = false;
    this.stopVideoStreaming();
  }

  initiateCaptureModal() {
    const self = this;
    // The width and height of the captured photo. We will set the
    // width to the value defined here, but the height will be
    // calculated based on the aspect ratio of the input stream.

    // |streaming| indicates whether or not we're currently streaming
    // video from the camera. Obviously, we start at false.
    let streaming = false;

    // The various HTML elements we need to configure or control. These
    // will be set by the startup() function.
    let startbutton = null;

    function startup() {
      const video = self.getVideoElement();
      const canvas = self.getCanvasElement();
      const photo = self.getPhotoElement();
      startbutton = document.getElementById('startbutton');

      navigator.mediaDevices.getUserMedia({ video: true, audio: false })
        .then((stream) => {
          video.srcObject = stream;
          video.play();
        })
        .catch((err) => {
          // console.log("An error occurred: " + err);
          alert('È necessario attivare la videocamera per poter utilizzare questa funzionalità');
        });

      video.addEventListener('canplay', (ev) => {
        if (!streaming) {
          self.canvasHeight = video.videoHeight / (video.videoWidth / self.canvasWidth);
          // Firefox currently has a bug where the height can't be read from
          // the video, so we will make assumptions if this happens.
          if (isNaN(self.canvasHeight)) {
            self.canvasHeight = self.canvasWidth / (4 / 3);
          }

          video.setAttribute('width', self.canvasWidth.toString());
          video.setAttribute('height', self.canvasHeight.toString());
          canvas.setAttribute('width', self.canvasWidth.toString());
          canvas.setAttribute('height', self.canvasHeight.toString());
          streaming = true;
        }
        setTimeout(() => {
          const camera = document.getElementsByClassName('camera')[0] as HTMLDivElement;
          const photoBox = document.getElementsByClassName('photo-box')[0] as HTMLDivElement;
          camera.style.height = `${(self.canvasHeight + 6).toString()}px`;
          photoBox.style.height = `${(self.canvasHeight + 6).toString()}px`;
        }, 10);
      }, false);

      startbutton.addEventListener('click', (ev) => {
        self.takePicture();
        ev.preventDefault();
      }, false);

      self.clearCanvas();
    }

    // Set up our event listener to run the startup process
    // once loading is complete.
    setTimeout(() => {
      startup();
    }, 100);
  }
}

